import { ref } from 'vue'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

// FAQ 카테고리
export const FAQ_CATEGORIES = [
  { id: 'service', label: '서비스 이용', icon: '📱' },
  { id: 'point', label: '포인트/생선', icon: '🐟' },
  { id: 'consultation', label: '상담 관련', icon: '💬' },
  { id: 'account', label: '계정/회원', icon: '👤' },
  { id: 'other', label: '기타', icon: '❓' }
]

export function useFaq() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * 모든 FAQ 조회 (Admin용)
   */
  const getAllFaqs = async () => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'faqs'),
        orderBy('order', 'asc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('FAQ 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 활성 FAQ 조회 (사용자용)
   */
  const getActiveFaqs = async () => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'faqs'),
        where('isActive', '==', true),
        orderBy('order', 'asc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('활성 FAQ 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 카테고리별 FAQ 조회
   */
  const getFaqsByCategory = async (category) => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'faqs'),
        where('isActive', '==', true),
        where('category', '==', category),
        orderBy('order', 'asc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('카테고리별 FAQ 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 단일 FAQ 조회
   */
  const getFaqById = async (faqId) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'faqs', faqId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        }
      }
      return null
    } catch (err) {
      console.error('FAQ 조회 실패:', err)
      error.value = err.message
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * FAQ 생성 (Admin)
   */
  const createFaq = async (faqData) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()

      // 현재 최대 order 값 조회
      const allFaqs = await getAllFaqs()
      const maxOrder = allFaqs.length > 0
        ? Math.max(...allFaqs.map(f => f.order || 0))
        : 0

      const docRef = await addDoc(collection(db, 'faqs'), {
        question: faqData.question,
        answer: faqData.answer,
        category: faqData.category || 'other',
        order: maxOrder + 1,
        isActive: true,
        createdAt: now,
        updatedAt: now
      })

      return docRef.id
    } catch (err) {
      console.error('FAQ 생성 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * FAQ 수정 (Admin)
   */
  const updateFaq = async (faqId, faqData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'faqs', faqId)
      await updateDoc(docRef, {
        ...faqData,
        updatedAt: new Date().toISOString()
      })
    } catch (err) {
      console.error('FAQ 수정 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * FAQ 활성화/비활성화 토글 (Admin)
   */
  const toggleFaqActive = async (faqId, isActive) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'faqs', faqId)
      await updateDoc(docRef, {
        isActive,
        updatedAt: new Date().toISOString()
      })
    } catch (err) {
      console.error('FAQ 상태 변경 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * FAQ 순서 변경 (Admin)
   */
  const updateFaqOrder = async (faqId, newOrder) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'faqs', faqId)
      await updateDoc(docRef, {
        order: newOrder,
        updatedAt: new Date().toISOString()
      })
    } catch (err) {
      console.error('FAQ 순서 변경 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * FAQ 삭제 (Admin)
   */
  const deleteFaq = async (faqId) => {
    loading.value = true
    error.value = null

    try {
      await deleteDoc(doc(db, 'faqs', faqId))
    } catch (err) {
      console.error('FAQ 삭제 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    getAllFaqs,
    getActiveFaqs,
    getFaqsByCategory,
    getFaqById,
    createFaq,
    updateFaq,
    toggleFaqActive,
    updateFaqOrder,
    deleteFaq
  }
}
